import { Can } from "@/components/citadel";
import { SettlementStateTag } from "@/components/partners";
import { client } from "@/hono";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@msuite/ui/button";
import { useLoading } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { useTimelineContext } from "@msuite/ui/timeline";
import { responseHandler } from "@msuite/ui/utils";
import { getSettlementRecordState } from "@msuite/utils/partnerships";
import { guardString } from "@msuite/utils/strings";
import type { Tables } from "@msuite/utils/types";
import { Fragment } from "react";

/** Props Interface */
interface PartnerTimelineAssignmentReferenceApproveButtonProps {
	reference: Pick<Tables<"ticketAssignmentReferences">, "id" | "isApproved" | "isReviewed">;
	partnershipId: string;
	/** The organisation that owns the plan and the project — the only side that approves. */
	hostOrgId: string;
	/** Whether the record holds what the host answers: bookings, or a service calculation. */
	hasBookings: boolean;
	/** Runs after an approval was toggled, beside the surrounding timeline's refetch. */
	onComplete?: () => Promise<unknown>;
}

export function PartnerTimelineAssignmentReferenceApproveButton({
	reference,
	partnershipId,
	hostOrgId,
	hasBookings,
	onComplete,
}: PartnerTimelineAssignmentReferenceApproveButtonProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { organization } = useAuth();
	const { refetch } = useTimelineContext();
	const loader = useLoading();
	const withdrawModal = useAsyncModal();

	/** Functions */
	async function handleToggleApproval() {
		// Approving is one click; taking an approval back reopens the bookings for both sides,
		// so it asks first.
		if (reference.isApproved && !(await withdrawModal.confirm())) return;
		try {
			loader.start();
			const response = await client.organizations[":orgId"].partnerships[
				":partnershipId"
			].reference[":referenceId"].approve.$patch({
				param: {
					orgId: guardString(organization?.id),
					partnershipId,
					referenceId: reference.id,
				},
				json: { isApproved: !reference.isApproved },
			});
			await responseHandler(response);
			await refetch();
			await onComplete?.();
		} catch {
			// `responseHandler` has already toasted the refusal; the record stays as it was.
		} finally {
			loader.stop();
		}
	}

	/** Computed */
	const isHost = organization?.id === hostOrgId;
	const state = getSettlementRecordState({
		isApproved: reference.isApproved,
		isReviewed: reference.isReviewed,
		hasBookings,
	});

	/** Guard */
	// The server lets only the host approve, so the partner is shown where the record stands
	// instead of a button whose every press would be refused.
	if (!isHost) return <SettlementStateTag state={state} />;

	/** Render */
	// The route behind this button gates on `approve Partner`, which is the one Partner action a
	// role can be denied while holding every other.
	return (
		<Fragment>
			<Can
				action="approve"
				subject="Partner"
			>
				<Button
					variant={reference.isApproved ? "success" : "white"}
					onClick={handleToggleApproval}
					isLoading={loader.isLoading}
					icon={<Icon name="BadgeCheck" />}
				>
					{reference.isApproved ? t("general.approved") : t("general.approve")}
				</Button>
			</Can>
			{withdrawModal.isOpen && (
				<ConfirmModal
					controller={withdrawModal}
					title={t("partners.withdraw_approval_title")}
					message={t("partners.withdraw_approval_message")}
					confirmLabel={t("partners.withdraw_approval")}
				/>
			)}
		</Fragment>
	);
}
